import { useEffect, useRef, useState, type ReactNode } from "react";

const USER = "hello";
const DOMAIN = "kinshipcarecollective.com";

interface EmailLinkProps {
  className?: string;
  children?: ReactNode;
  /** Optional aria-label override. */
  label?: string;
}

/**
 * Renders the contact email without ever putting a plain `mailto:` address in
 * the server-delivered HTML, so spam harvesters that don't run JS see nothing
 * useful. The real href is assembled in the browser after mount.
 */
const EmailLink = ({ className, children, label }: EmailLinkProps) => {
  const [href, setHref] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    setHref(`mailto:${USER}\u0040${DOMAIN}`);
  }, []);

  // When custom children are supplied, their visible text is the accessible
  // name — overriding it with a different label breaks "label in name".
  const ariaLabel = label ?? (children ? undefined : `Email ${USER}\u0040${DOMAIN}`);

  return (
    <a
      ref={ref}
      href={href}
      aria-label={ariaLabel}
      className={className}
      onClick={(e) => {
        if (!href) {
          e.preventDefault();
          window.location.href = `mailto:${USER}\u0040${DOMAIN}`;
        }
      }}
    >
      {children ?? (
        <span>
          {USER}
          <span aria-hidden="true">&#64;</span>
          <span className="sr-only">@</span>
          {DOMAIN}
        </span>
      )}
    </a>
  );
};

export const EMAIL_TEXT = `${USER}@${DOMAIN}`;

export default EmailLink;
